import { useState, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lucide icon name → component mapping
// We use a string-keyed lookup so the picker stays dynamic as categories change.
import {
  Laptop, Sofa, Shirt, Car, Bike, Home, Trophy, BookOpen,
  Wrench, Baby, Music, PawPrint, Package,
} from 'lucide-react';

const ICON_MAP = {
  Laptop, Sofa, Shirt, Car, Bike, Home, Trophy, BookOpen,
  Wrench, Baby, Music, PawPrint, Package,
};

function CategoryIcon({ name, className }) {
  const Icon = ICON_MAP[name] || Package;
  return <Icon className={className} />;
}

/**
 * CategoryPreferencePicker
 *
 * Props:
 *   selectedCategories     – string[]  currently selected category IDs
 *   selectedSubcategories  – string[]  currently selected subcategory IDs
 *   onChange               – ({ categories, subcategories }) => void
 *   onSubmit               – () => void  called when "Continue" is pressed
 *   onSkip                 – () => void  called when "Skip" is pressed (optional)
 *   submitLabel            – string  button label (default "Continue")
 */
export default function CategoryPreferencePicker({
  selectedCategories = [],
  selectedSubcategories = [],
  onChange,
  onSubmit,
  onSkip,
  submitLabel = 'Continue',
}) {
  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          // API returns either an array directly or { categories: [...] }
          const list = Array.isArray(data) ? data : data.categories ?? [];
          setCategories(list);
        }
      } catch {
        // Silently fail; picker will be empty but won't block registration
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const toggleCategory = (catId) => {
    const next = selectedCategories.includes(catId)
      ? selectedCategories.filter((id) => id !== catId)
      : [...selectedCategories, catId];

    // Deselect subcategories for a category that was just deselected
    const cat = categories.find((c) => c.id === catId);
    let nextSubs = selectedSubcategories;
    if (!next.includes(catId) && cat?.subcategories?.length) {
      const subIds = new Set(cat.subcategories.map((s) => s.id));
      nextSubs = selectedSubcategories.filter((id) => !subIds.has(id));
    }

    onChange({ categories: next, subcategories: nextSubs });
  };

  const toggleSubcategory = (subId) => {
    const next = selectedSubcategories.includes(subId)
      ? selectedSubcategories.filter((id) => id !== subId)
      : [...selectedSubcategories, subId];
    onChange({ categories: selectedCategories, subcategories: next });
  };

  const toggleExpand = (catId) => {
    setExpandedCategory((prev) => (prev === catId ? null : catId));
  };

  const totalSelected = selectedCategories.length + selectedSubcategories.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-[var(--theme-text-muted)] mb-4">
        {totalSelected === 0
          ? 'None selected'
          : `${totalSelected} selected`}
      </p>

      <div className="grid grid-cols-2 gap-2 mb-6">
        {categories.map((cat) => {
          const isSelected = selectedCategories.includes(cat.id);
          const isExpanded = expandedCategory === cat.id;
          const hasSubs = cat.subcategories?.length > 0;

          return (
            <div key={cat.id}>
              {/* Category card */}
              <button
                type="button"
                onClick={() => toggleCategory(cat.id)}
                className={[
                  'w-full flex items-center gap-2 px-3 py-3 rounded-xl border-2 transition-all duration-150 text-left',
                  isSelected
                    ? 'border-[#1E3A5F] bg-[#1E3A5F]/8 dark:border-[#C9956C] dark:bg-[#C9956C]/10'
                    : 'border-[#E5DDD3] bg-[var(--theme-card)] hover:border-[#D4CCC2]',
                ].join(' ')}
              >
                <div
                  className={[
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    isSelected ? 'bg-[#1E3A5F] dark:bg-[#C9956C]' : 'bg-[var(--theme-hover)]',
                  ].join(' ')}
                >
                  {isSelected ? (
                    <Check className="w-4 h-4 text-white" />
                  ) : (
                    <CategoryIcon
                      name={cat.icon}
                      className="w-4 h-4 text-[var(--theme-text-secondary)]"
                    />
                  )}
                </div>
                <span
                  className={[
                    'flex-1 text-xs font-medium leading-tight',
                    isSelected
                      ? 'text-[#1E3A5F] dark:text-[#C9956C]'
                      : 'text-[var(--theme-text-primary)]',
                  ].join(' ')}
                >
                  {cat.name}
                </span>
                {hasSubs && isSelected && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleExpand(cat.id); }}
                    className="ml-1 text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)]"
                    aria-label={isExpanded ? 'Collapse subcategories' : 'Expand subcategories'}
                  >
                    {isExpanded
                      ? <ChevronUp className="w-3 h-3" />
                      : <ChevronDown className="w-3 h-3" />}
                  </button>
                )}
              </button>

              {/* Subcategory chips */}
              {hasSubs && isSelected && isExpanded && (
                <div className="mt-1 flex flex-wrap gap-1.5 pl-1">
                  {cat.subcategories.map((sub) => {
                    const subSelected = selectedSubcategories.includes(sub.id);
                    return (
                      <button
                        key={sub.id}
                        type="button"
                        onClick={() => toggleSubcategory(sub.id)}
                        className={[
                          'px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150',
                          subSelected
                            ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white dark:border-[#C9956C] dark:bg-[#C9956C]'
                            : 'border-[#E5DDD3] bg-[var(--theme-bg)] text-[var(--theme-text-secondary)] hover:border-[#D4CCC2]',
                        ].join(' ')}
                      >
                        {sub.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        onClick={onSubmit}
        className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12 dark:bg-[#C9956C] dark:text-[#1E3A5F] dark:hover:bg-[#B8845B]"
      >
        {submitLabel}
      </Button>

      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="w-full mt-3 text-sm text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)] transition-colors"
        >
          Skip for now
        </button>
      )}
    </div>
  );
}
